import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  Mail, Phone, MapPin, ExternalLink, Save, X, Loader2, ShoppingCart, Bell, BellOff, Plus, Trash2,
  ChevronDown, KeyRound, Link2, CalendarClock, Images,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { customerService } from '../services/customerService';
import { orderService } from '../services/orderService';
import { invoiceService } from '../services/invoiceService';
import type { ReminderSettings } from '../types/domain';
import { useLoader } from '../contexts/LoaderContext';
import type { Customer, Address, Order } from '../types/domain';
import { cn } from '@/lib/utils';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  Editable, EditableArea, EditablePreview, EditableInput,
} from '@/components/ui/editable';

// ---------------------------------------------------------------------------
// File-scope helpers
// ---------------------------------------------------------------------------

const ADDRESS_FIELDS: { key: keyof Address; label: string }[] = [
  { key: 'address', label: 'Address' },
  { key: 'street2', label: 'Address 2' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip', label: 'Postcode' },
  { key: 'country', label: 'Country' },
];

const formatCurrency = (value: number | undefined | null): string => {
  if (value === undefined || value === null || isNaN(value)) return '\u00A30';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
};

const formatDate = (dateString: string | undefined | null) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return '-'; }
};

const orderStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'draft':
      return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    case 'confirmed': case 'fulfilled':
      return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
    case 'shipped': case 'partially_shipped':
      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'invoiced': case 'partially_invoiced':
      return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    case 'void': case 'cancelled':
      return 'bg-destructive/20 text-destructive border-destructive/30';
    default:
      return 'bg-muted-foreground/10 text-muted-foreground border-muted-foreground/20';
  }
};

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-border/30 last:border-0">
      <span className="text-[13px] text-muted-foreground shrink-0">{label}</span>
      <div className="text-right">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CustomerDetail() {
  const [pageTitle, setPageTitle] = useState('Customer');
  usePageTitle(pageTitle);
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const [customerData, setCustomerData] = useState<Customer | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Inline edit state
  const [dirtyFields, setDirtyFields] = useState<Record<string, unknown>>({});
  const [discardCount, setDiscardCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Login & Access state
  const [resettingPin, setResettingPin] = useState(false);
  const [sendingMagicLink, setSendingMagicLink] = useState(false);
  const [creatingAccessLink, setCreatingAccessLink] = useState(false);

  // Reminder settings state
  const [reminderSettings, setReminderSettings] = useState<ReminderSettings | null>(null);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderSaving, setReminderSaving] = useState(false);
  const [reminderDayInput, setReminderDayInput] = useState('');
  const [reminderAfterDayInput, setReminderAfterDayInput] = useState('');
  const [remindersOpen, setRemindersOpen] = useState(false);

  const hasDirtyFields = Object.keys(dirtyFields).length > 0;

  useEffect(() => {
    if (customerId) fetchCustomerData();
  }, [customerId]);

  useEffect(() => {
    if (customerData?.zoho_contact_id) {
      fetchOrders(customerData.zoho_contact_id);
    }
  }, [customerData?.zoho_contact_id]);

  useEffect(() => {
    if (customerData?.id) {
      fetchReminderSettings(customerData.id);
    }
  }, [customerData?.id]);

  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      showLoader('Loading Customer...');
      const customer = await customerService.getById(customerId!);
      setCustomerData(customer);
      if (customer?.company_name) setPageTitle(customer.company_name);
    } catch (error) {
      console.error('Error fetching customer:', error);
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const fetchOrders = async (zohoContactId: string) => {
    try {
      setOrdersLoading(true);
      const { data } = await orderService.list({ customer_id: zohoContactId, limit: 50 });
      setOrders(data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchReminderSettings = async (custId: number) => {
    try {
      setReminderLoading(true);
      const settings = await invoiceService.getReminderSettings(custId);
      setReminderSettings(settings);
    } catch {
      // No settings yet — use defaults
      setReminderSettings({
        customer_id: custId,
        is_enabled: true,
        days_before_due: [7, 3, 1],
        days_after_due: [1, 7, 14, 30],
        max_reminders: 5,
        cc_agent: true,
        custom_message: null,
      });
    } finally {
      setReminderLoading(false);
    }
  };

  const handleSaveReminderSettings = async () => {
    if (!customerData?.id || !reminderSettings) return;
    try {
      setReminderSaving(true);
      const updated = await invoiceService.updateReminderSettings(customerData.id, reminderSettings);
      setReminderSettings(updated);
    } catch (err) {
      console.error('Failed to save reminder settings:', err);
    } finally {
      setReminderSaving(false);
    }
  };

  const addDayBefore = () => {
    const day = parseInt(reminderDayInput);
    if (!reminderSettings || isNaN(day) || day < 1 || day > 90) return;
    if (reminderSettings.days_before_due.includes(day)) return;
    setReminderSettings({
      ...reminderSettings,
      days_before_due: [...reminderSettings.days_before_due, day].sort((a, b) => b - a),
    });
    setReminderDayInput('');
  };

  const removeDayBefore = (day: number) => {
    if (!reminderSettings) return;
    setReminderSettings({
      ...reminderSettings,
      days_before_due: reminderSettings.days_before_due.filter(d => d !== day),
    });
  };

  const addDayAfter = () => {
    const day = parseInt(reminderAfterDayInput);
    if (!reminderSettings || isNaN(day) || day < 1 || day > 90) return;
    if (reminderSettings.days_after_due.includes(day)) return;
    setReminderSettings({
      ...reminderSettings,
      days_after_due: [...reminderSettings.days_after_due, day].sort((a, b) => a - b),
    });
    setReminderAfterDayInput('');
  };

  const removeDayAfter = (day: number) => {
    if (!reminderSettings) return;
    setReminderSettings({
      ...reminderSettings,
      days_after_due: reminderSettings.days_after_due.filter(d => d !== day),
    });
  };

  // -- Dirty field tracking --
  const handleFieldSubmit = useCallback((field: string, value: string) => {
    if (!customerData) return;
    const currentVal = String((customerData as unknown as Record<string, unknown>)[field] ?? '');
    if (value === currentVal) {
      setDirtyFields(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
      return;
    }
    setDirtyFields(prev => ({ ...prev, [field]: value || null }));
  }, [customerData]);

  const handleAddressFieldSubmit = useCallback((type: 'billing_address' | 'shipping_address', field: keyof Address, value: string) => {
    if (!customerData) return;
    const currentAddr = customerData[type] as Address | null;
    const currentVal = currentAddr?.[field] ?? '';
    const dirtyKey = `${type}.${field}`;

    if (value === currentVal) {
      setDirtyFields(prev => {
        const next = { ...prev };
        delete next[dirtyKey];
        return next;
      });
      return;
    }
    setDirtyFields(prev => ({ ...prev, [dirtyKey]: value || undefined }));
  }, [customerData]);

  const handleSave = async () => {
    if (!customerData || !hasDirtyFields) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Reconstruct update payload from dirtyFields
      const payload: Partial<Customer> = {};
      const billingUpdates: Record<string, unknown> = {};
      const shippingUpdates: Record<string, unknown> = {};

      for (const [key, value] of Object.entries(dirtyFields)) {
        if (key.startsWith('billing_address.')) {
          billingUpdates[key.replace('billing_address.', '')] = value;
        } else if (key.startsWith('shipping_address.')) {
          shippingUpdates[key.replace('shipping_address.', '')] = value;
        } else {
          (payload as Record<string, unknown>)[key] = value;
        }
      }

      if (Object.keys(billingUpdates).length > 0) {
        payload.billing_address = { ...(customerData.billing_address as Address || {}), ...billingUpdates } as Address;
      }
      if (Object.keys(shippingUpdates).length > 0) {
        payload.shipping_address = { ...(customerData.shipping_address as Address || {}), ...shippingUpdates } as Address;
      }

      const updated = await customerService.update(customerData.id, payload);
      setCustomerData(updated);
      setDirtyFields({});
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save changes';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setDirtyFields({});
    setSaveError(null);
    setDiscardCount(c => c + 1);
  };

  const handleResetPin = async () => {
    if (!customerData || !window.confirm('Reset this customer\'s PIN to default? They will need to set a new PIN on next login.')) return;
    setResettingPin(true);
    try {
      const result = await customerService.resetPin(customerData.id);
      alert(result.message || 'PIN reset successfully');
    } catch (err) {
      console.error('Error resetting PIN:', err);
      alert('Failed to reset PIN');
    } finally {
      setResettingPin(false);
    }
  };

  const handleSendMagicLink = async () => {
    if (!customerData || !window.confirm(`Send a magic link to ${customerData.email}?`)) return;
    setSendingMagicLink(true);
    try {
      const result = await customerService.sendMagicLink(customerData.id);
      alert(result.message || 'Magic link sent');
    } catch (err) {
      console.error('Error sending magic link:', err);
      alert('Failed to send magic link');
    } finally {
      setSendingMagicLink(false);
    }
  };

  const handleCreateAccessLink = async () => {
    if (!customerData) return;
    setCreatingAccessLink(true);
    try {
      const link = await customerService.createAccessLink(customerData.id, { days: 30 });
      const validUntil = new Date(link.expires_at).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric',
      });
      let copied = false;
      try {
        await navigator.clipboard.writeText(link.url);
        copied = true;
      } catch {
        // Clipboard unavailable (insecure context / permission); fall back to prompt below.
      }

      const emailIt = customerData.email
        ? window.confirm(
            `30-day sign-in link ${copied ? 'copied to clipboard' : 'created'} (valid until ${validUntil}).\n\n` +
            `Also email it to ${customerData.email}?`,
          )
        : false;

      if (emailIt) {
        await customerService.createAccessLink(customerData.id, { days: 30, send: true });
        alert(`Link emailed to ${customerData.email}`);
      } else if (!copied) {
        window.prompt('Copy this link:', link.url);
      }
    } catch (err) {
      console.error('Error creating access link:', err);
      alert('Failed to create 30-day link');
    } finally {
      setCreatingAccessLink(false);
    }
  };

  if (loading) return null;

  if (!customerData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-muted-foreground">Customer not found</p>
        <Button intent="plain" size="sm" onPress={() => navigate('/customers')}>
          Back to Customers
        </Button>
      </div>
    );
  }

  const contactPersons = customerData.contact_persons || [];
  const primaryContact = contactPersons.find(c => c.is_primary_contact);
  const outstandingAmount = customerData.outstanding_receivable || 0;
  const lastOrderDate = orders.length > 0 ? orders[0]?.date : undefined;

  // Stable Editable key prefix
  const ek = `${customerData.id}-${discardCount}`;

  // Helpers for flat address rendering
  const billingFields = ADDRESS_FIELDS.filter(({ key }) => {
    const val = (customerData.billing_address as Address)?.[key];
    return val && val.trim() !== '';
  });
  const shippingFields = ADDRESS_FIELDS.filter(({ key }) => {
    const val = (customerData.shipping_address as Address)?.[key];
    return val && val.trim() !== '';
  });

  return (
    <div className="w-full p-6 max-w-[1400px]">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-5">
        <button onClick={() => navigate('/customers')} className="hover:text-foreground transition-colors">
          Customers
        </button>
        <span className="text-muted-foreground/50">/</span>
        <span className="text-foreground font-medium truncate max-w-[300px]">{customerData.company_name}</span>
      </nav>

      {/* ── Identity + Metrics strip ── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-6 pb-7 mb-8 border-b border-border/60"
      >
        <div className="flex items-center gap-3 shrink-0">
          <Avatar
            size="lg"
            initials={customerData.company_name.charAt(0).toUpperCase()}
            className="bg-primary text-primary-fg"
          />
          <div>
            <h1 className="text-[17px] font-semibold text-foreground leading-tight">{customerData.company_name}</h1>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className={cn(
                'inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider',
                customerData.status === 'active'
                  ? 'bg-success/10 text-success'
                  : 'bg-muted/30 text-muted-foreground'
              )}>
                {customerData.status}
              </span>
              {customerData.location_region && (
                <span className="text-[11px] text-muted-foreground">{customerData.location_region}</span>
              )}
              {customerData.brand_preferences && customerData.brand_preferences.length > 0 && (
                <>
                  <span className="text-border">&middot;</span>
                  {customerData.brand_preferences.map((brand, i) => {
                    const label = typeof brand === 'string' ? brand : (brand as Record<string, string>)?.brand || String(brand);
                    return (
                      <span
                        key={i}
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-primary/8 text-primary border border-primary/15"
                      >
                        {label}
                      </span>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>

        <Separator orientation="vertical" className="h-10" />

        {/* Metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-7 gap-x-6 gap-y-3 flex-1 min-w-0">
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Spent</div>
            <div className="text-lg font-semibold text-foreground tabular-nums">{formatCurrency(customerData.total_spent)}</div>
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Outstanding</div>
            <div className={cn('text-lg font-semibold tabular-nums', outstandingAmount > 0 ? 'text-warning' : 'text-muted-foreground')}>
              {outstandingAmount > 0 ? formatCurrency(outstandingAmount) : '\u00A30'}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Terms</div>
            <div className="text-lg font-semibold text-foreground">{customerData.payment_terms_label || customerData.payment_terms || '-'}</div>
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Orders</div>
            <div className="text-lg font-semibold text-foreground tabular-nums">{orders.length}</div>
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Avg Order</div>
            <div className="text-lg font-semibold text-foreground tabular-nums">{formatCurrency(customerData.average_order_value)}</div>
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Since</div>
            <div className="text-sm font-medium text-foreground">{formatDate(customerData.created_at)}</div>
          </div>
          {customerData.segment && (
            <div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Segment</div>
              <div className="text-sm font-medium text-primary">{customerData.segment}</div>
            </div>
          )}
        </div>

        {/* Header actions */}
        <div className="shrink-0 flex items-center gap-2">
          <AnimatePresence>
            {hasDirtyFields && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex items-center gap-2"
              >
                <Button intent="outline" size="sm" onPress={handleDiscard} isDisabled={saving}>
                  <X size={13} /> Discard
                </Button>
                <Button intent="primary" size="sm" onPress={handleSave} isDisabled={saving}>
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
          {!hasDirtyFields && (
            <Button
              intent="outline"
              size="sm"
              onPress={() => navigate('/orders/new', { state: { fromCustomerDetail: true, customer: { id: customerData.id, display_name: customerData.company_name } } })}
            >
              <ShoppingCart size={12} /> New Order
            </Button>
          )}
        </div>
      </motion.div>

      {/* Save error banner */}
      <AnimatePresence>
        {saveError && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 px-4 py-2.5 rounded-md bg-destructive/10 border border-destructive/20 text-[13px] text-destructive"
          >
            {saveError}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Main two-column layout ── */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-x-12 gap-y-10">

        {/* ── Left column: Details + Financial merged flat ── */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <h3 className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider mb-4">Details</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
            {/* Left sub-column: Editable company fields */}
            <div>
              <DetailRow label="Contact">
                <Editable
                  key={`contact_name-${ek}`}
                  defaultValue={customerData.contact_name || ''}
                  placeholder="-"
                  onSubmit={val => handleFieldSubmit('contact_name', val)}
                >
                  <EditableArea>
                    <EditablePreview className="text-[13px] font-medium text-foreground py-0" />
                    <EditableInput className="text-[13px] font-medium px-1 text-right" />
                  </EditableArea>
                </Editable>
              </DetailRow>

              <DetailRow label="Email">
                <div className="flex items-center gap-1.5 justify-end">
                  <Editable
                    key={`email-${ek}`}
                    defaultValue={customerData.email || primaryContact?.email || ''}
                    placeholder="-"
                    onSubmit={val => handleFieldSubmit('email', val)}
                  >
                    <EditableArea>
                      <EditablePreview className="text-[13px] font-medium text-foreground py-0" />
                      <EditableInput className="text-[13px] font-medium px-1 text-right" type="email" />
                    </EditableArea>
                  </Editable>
                  {(customerData.email || primaryContact?.email) && (
                    <Tooltip>
                      <TooltipTrigger
                        aria-label="Send email"
                        className="p-0.5 rounded text-muted-foreground hover:text-primary transition-colors"
                        onPress={() => window.open(`mailto:${customerData.email || primaryContact?.email}`)}
                      >
                        <Mail size={11} />
                      </TooltipTrigger>
                      <TooltipContent>Send email</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </DetailRow>

              <DetailRow label="Phone">
                <div className="flex items-center gap-1.5 justify-end">
                  <Editable
                    key={`phone-${ek}`}
                    defaultValue={customerData.phone || ''}
                    placeholder="-"
                    onSubmit={val => handleFieldSubmit('phone', val)}
                  >
                    <EditableArea>
                      <EditablePreview className="text-[13px] font-medium text-foreground py-0 tabular-nums" />
                      <EditableInput className="text-[13px] font-medium px-1 text-right" type="tel" />
                    </EditableArea>
                  </Editable>
                  {customerData.phone && (
                    <Tooltip>
                      <TooltipTrigger
                        aria-label="Call"
                        className="p-0.5 rounded text-muted-foreground hover:text-primary transition-colors"
                        onPress={() => window.open(`tel:${customerData.phone}`)}
                      >
                        <Phone size={11} />
                      </TooltipTrigger>
                      <TooltipContent>Call</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </DetailRow>

              <DetailRow label="Mobile">
                <div className="flex items-center gap-1.5 justify-end">
                  <Editable
                    key={`mobile-${ek}`}
                    defaultValue={customerData.mobile || ''}
                    placeholder="-"
                    onSubmit={val => handleFieldSubmit('mobile', val)}
                  >
                    <EditableArea>
                      <EditablePreview className="text-[13px] font-medium text-foreground py-0 tabular-nums" />
                      <EditableInput className="text-[13px] font-medium px-1 text-right" type="tel" />
                    </EditableArea>
                  </Editable>
                  {customerData.mobile && (
                    <Tooltip>
                      <TooltipTrigger
                        aria-label="Call mobile"
                        className="p-0.5 rounded text-muted-foreground hover:text-primary transition-colors"
                        onPress={() => window.open(`tel:${customerData.mobile}`)}
                      >
                        <Phone size={11} />
                      </TooltipTrigger>
                      <TooltipContent>Call mobile</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </DetailRow>

              <DetailRow label="Website">
                <Editable
                  key={`website-${ek}`}
                  defaultValue={customerData.website || ''}
                  placeholder="-"
                  onSubmit={val => handleFieldSubmit('website', val)}
                >
                  <EditableArea>
                    <EditablePreview className="text-[13px] font-medium text-foreground py-0" />
                    <EditableInput className="text-[13px] font-medium px-1 text-right" />
                  </EditableArea>
                </Editable>
              </DetailRow>

              <DetailRow label="Payment Terms">
                <Editable
                  key={`payment_terms-${ek}`}
                  defaultValue={customerData.payment_terms || ''}
                  placeholder="-"
                  onSubmit={val => handleFieldSubmit('payment_terms', val)}
                >
                  <EditableArea>
                    <EditablePreview className="text-[13px] font-medium text-foreground py-0" />
                    <EditableInput className="text-[13px] font-medium px-1 text-right" />
                  </EditableArea>
                </Editable>
              </DetailRow>
            </div>

            {/* Right sub-column: Read-only financial + access */}
            <div>
              <DetailRow label="Total Spent">
                <span className="text-[13px] font-semibold text-primary tabular-nums">{formatCurrency(customerData.total_spent)}</span>
              </DetailRow>
              {outstandingAmount > 0 && (
                <DetailRow label="Outstanding">
                  <span className="text-[13px] font-medium text-warning tabular-nums">{formatCurrency(outstandingAmount)}</span>
                </DetailRow>
              )}
              {(customerData.unused_credits ?? 0) > 0 && (
                <DetailRow label="Unused Credits">
                  <span className="text-[13px] font-medium text-foreground tabular-nums">{formatCurrency(customerData.unused_credits)}</span>
                </DetailRow>
              )}
              <DetailRow label="Currency">
                <span className="text-[13px] font-medium text-foreground">{customerData.currency_code}</span>
              </DetailRow>
              <DetailRow label="Last Order">
                <span className="text-[13px] font-medium text-foreground">{formatDate(lastOrderDate)}</span>
              </DetailRow>

              {/* Login & Access — inline, no card */}
              <div className="flex items-center gap-2 pt-4 mt-1 border-t border-border/30">
                <Button intent="outline" size="sm" onPress={handleResetPin} isDisabled={resettingPin}>
                  {resettingPin ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={12} />}
                  Reset PIN
                </Button>
                <Button intent="outline" size="sm" onPress={handleSendMagicLink} isDisabled={sendingMagicLink || !customerData?.email}>
                  {sendingMagicLink ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={12} />}
                  Magic Link
                </Button>
                <Button intent="outline" size="sm" onPress={handleCreateAccessLink} isDisabled={creatingAccessLink}>
                  {creatingAccessLink ? <Loader2 size={13} className="animate-spin" /> : <CalendarClock size={12} />}
                  30-day Link
                </Button>
                <Button
                  intent="outline"
                  size="sm"
                  onPress={() => navigate(`/quotes/new?customer=${customerData.id}`)}
                >
                  <Images size={12} />
                  Photo Quote
                </Button>
              </div>

              {/* Integration — inline, no card */}
              {customerData.zoho_contact_id && (
                <div className="pt-3 mt-3 border-t border-border/30">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground/60">Zoho</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-mono text-muted-foreground">{customerData.zoho_contact_id}</span>
                      <span className={cn(
                        'text-[10px] px-1.5 py-0.5 rounded-md border font-medium',
                        customerData.sync_status === 'synced'
                          ? 'bg-emerald-500/8 text-emerald-400 border-emerald-500/15'
                          : customerData.sync_status === 'pending_push'
                            ? 'bg-amber-500/8 text-amber-400 border-amber-500/15'
                            : 'bg-red-500/8 text-red-400 border-red-500/15'
                      )}>
                        {customerData.sync_status === 'synced' ? 'Synced' : customerData.sync_status === 'pending_push' ? 'Pending' : customerData.sync_status}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* ── Right column ── */}
        <div className="flex flex-col gap-8">
          {/* Contacts — flat */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h3 className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider mb-3">
              Contacts ({contactPersons.length})
            </h3>
            {contactPersons.length > 0 ? (
              <div className="space-y-3">
                {contactPersons.map((contact, i) => (
                  <div key={contact.contact_person_id || i} className="py-2 border-b border-border/30 last:border-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[13px] font-medium text-foreground">
                        {`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unnamed'}
                      </span>
                      {contact.is_primary_contact && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-primary/8 text-primary border border-primary/15">
                          Primary
                        </span>
                      )}
                    </div>
                    {contact.email && (
                      <a href={`mailto:${contact.email}`} className="flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-primary transition-colors mb-0.5">
                        <Mail size={11} /> {contact.email}
                      </a>
                    )}
                    {contact.phone && (
                      <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                        <Phone size={11} /> {contact.phone}
                      </div>
                    )}
                    {contact.mobile && (
                      <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                        <Phone size={11} /> {contact.mobile}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[13px] text-muted-foreground">No contacts</p>
            )}
          </motion.div>

          {/* Addresses — flat, side by side */}
          {(billingFields.length > 0 || shippingFields.length > 0) && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <h3 className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider mb-3">Addresses</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Billing */}
                {billingFields.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <MapPin size={11} className="text-muted-foreground" />
                      <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Billing</span>
                    </div>
                    <div className="space-y-1">
                      {billingFields.map(({ key, label }) => (
                        <div key={key} className="flex items-center justify-between">
                          <span className="text-[11px] text-muted-foreground/60">{label}</span>
                          <Editable
                            key={`billing_${key}-${ek}`}
                            defaultValue={(customerData.billing_address as Address)?.[key] ?? ''}
                            placeholder="-"
                            onSubmit={val => handleAddressFieldSubmit('billing_address', key, val)}
                          >
                            <EditableArea>
                              <EditablePreview className="text-[13px] text-foreground py-0" />
                              <EditableInput className="text-[13px] px-1 text-right" />
                            </EditableArea>
                          </Editable>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Shipping */}
                {shippingFields.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <MapPin size={11} className="text-muted-foreground" />
                      <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Shipping</span>
                    </div>
                    <div className="space-y-1">
                      {shippingFields.map(({ key, label }) => (
                        <div key={key} className="flex items-center justify-between">
                          <span className="text-[11px] text-muted-foreground/60">{label}</span>
                          <Editable
                            key={`shipping_${key}-${ek}`}
                            defaultValue={(customerData.shipping_address as Address)?.[key] ?? ''}
                            placeholder="-"
                            onSubmit={val => handleAddressFieldSubmit('shipping_address', key, val)}
                          >
                            <EditableArea>
                              <EditablePreview className="text-[13px] text-foreground py-0" />
                              <EditableInput className="text-[13px] px-1 text-right" />
                            </EditableArea>
                          </Editable>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>

        {/* ── Orders — full-width spanning both columns ── */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="xl:col-span-2"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider">
              Orders ({orders.length})
            </h3>
            {orders.length > 0 && (
              <Button
                intent="plain"
                size="xs"
                onPress={() => navigate('/orders', { state: { customerId: customerData.id, customerName: customerData.company_name } })}
                className="text-primary/70 hover:text-primary"
              >
                View all <ExternalLink size={10} />
              </Button>
            )}
          </div>

          {ordersLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading orders...</div>
          ) : orders.length === 0 ? (
            <div className="py-10 text-center">
              <ShoppingCart size={24} className="mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground mb-3">No orders yet</p>
              <Button
                intent="plain"
                size="sm"
                onPress={() => navigate('/orders/new', { state: { fromCustomerDetail: true, customer: { id: customerData.id, display_name: customerData.company_name } } })}
                className="text-primary hover:underline"
              >
                Create first order
              </Button>
            </div>
          ) : (
            <div className="rounded-lg border border-border/60 overflow-hidden">
              <Table>
                <TableHeader className="bg-secondary/50">
                  <TableRow className="border-b border-border/60 hover:bg-secondary/50">
                    <TableHead>Order</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-[40px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.slice(0, 15).map(order => (
                    <TableRow
                      key={order.id}
                      className="cursor-pointer border-b border-border/30 hover:bg-primary/[0.03] transition-colors"
                      onClick={() => navigate(`/order/${order.id}`)}
                    >
                      <TableCell className="font-medium text-foreground">
                        <span className="text-[13px]">#{order.salesorder_number || order.id}</span>
                        {order.reference_number && (
                          <span className="ml-2 text-[11px] text-muted-foreground">{order.reference_number}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-[12px] text-muted-foreground tabular-nums">
                        {formatDate(order.date)}
                      </TableCell>
                      <TableCell>
                        <span className={cn(
                          'inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium capitalize border',
                          orderStatusColor(order.status)
                        )}>
                          {order.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-[13px] font-semibold text-foreground tabular-nums">
                          {formatCurrency(order.total)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <ExternalLink size={12} className="text-muted-foreground inline-block" />
                      </TableCell>
                    </TableRow>
                  ))}
                  {orders.length > 15 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-2">
                        <Button
                          intent="plain"
                          size="xs"
                          onPress={() => navigate('/orders', { state: { customerId: customerData.id, customerName: customerData.company_name } })}
                          className="text-primary/70 hover:text-primary"
                        >
                          View all {orders.length} orders
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </motion.div>

        {/* ── Invoice Reminders — collapsible, full-width ── */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="xl:col-span-2"
        >
          <button
            onClick={() => setRemindersOpen(prev => !prev)}
            className="flex items-center gap-2 w-full group"
          >
            <ChevronDown
              size={14}
              className={cn(
                'text-muted-foreground/60 transition-transform duration-200',
                !remindersOpen && '-rotate-90'
              )}
            />
            <h3 className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider">
              Invoice Reminders
            </h3>
            {reminderSettings && (
              <span className={cn(
                'ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium',
                reminderSettings.is_enabled
                  ? 'text-emerald-400'
                  : 'text-muted-foreground/50'
              )}>
                {reminderSettings.is_enabled ? 'Enabled' : 'Disabled'}
              </span>
            )}
          </button>

          <AnimatePresence initial={false}>
            {remindersOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                {reminderLoading ? (
                  <div className="py-4 text-sm text-muted-foreground">Loading...</div>
                ) : reminderSettings ? (
                  <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
                    {/* Before due */}
                    <div>
                      <span className="text-[11px] text-muted-foreground/60 uppercase tracking-wider block mb-2">Before due date</span>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {reminderSettings.days_before_due.map(day => (
                          <span key={day} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[12px] font-medium border border-primary/15">
                            {day}d
                            <button onClick={() => removeDayBefore(day)} className="text-primary/50 hover:text-primary ml-0.5"><X size={10} /></button>
                          </span>
                        ))}
                        {reminderSettings.days_before_due.length === 0 && (
                          <span className="text-[12px] text-muted-foreground/50">None</span>
                        )}
                      </div>
                      <div className="flex gap-1.5">
                        <input
                          type="number"
                          min={1}
                          max={90}
                          placeholder="Days"
                          value={reminderDayInput}
                          onChange={e => setReminderDayInput(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && addDayBefore()}
                          className="w-16 px-2 py-1 text-[12px] bg-foreground/5 border border-foreground/10 rounded-md text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary/40"
                        />
                        <button onClick={addDayBefore} className="p-1 rounded-md bg-foreground/5 border border-foreground/10 text-muted-foreground hover:text-foreground hover:bg-foreground/10 transition-colors">
                          <Plus size={12} />
                        </button>
                      </div>
                    </div>

                    {/* After due */}
                    <div>
                      <span className="text-[11px] text-muted-foreground/60 uppercase tracking-wider block mb-2">After due date</span>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {reminderSettings.days_after_due.map(day => (
                          <span key={day} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-400 text-[12px] font-medium border border-amber-500/15">
                            {day}d
                            <button onClick={() => removeDayAfter(day)} className="text-amber-400/50 hover:text-amber-400 ml-0.5"><X size={10} /></button>
                          </span>
                        ))}
                        {reminderSettings.days_after_due.length === 0 && (
                          <span className="text-[12px] text-muted-foreground/50">None</span>
                        )}
                      </div>
                      <div className="flex gap-1.5">
                        <input
                          type="number"
                          min={1}
                          max={90}
                          placeholder="Days"
                          value={reminderAfterDayInput}
                          onChange={e => setReminderAfterDayInput(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && addDayAfter()}
                          className="w-16 px-2 py-1 text-[12px] bg-foreground/5 border border-foreground/10 rounded-md text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary/40"
                        />
                        <button onClick={addDayAfter} className="p-1 rounded-md bg-foreground/5 border border-foreground/10 text-muted-foreground hover:text-foreground hover:bg-foreground/10 transition-colors">
                          <Plus size={12} />
                        </button>
                      </div>
                    </div>

                    {/* Settings column */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] text-muted-foreground/60 uppercase tracking-wider">Max reminders</span>
                        <input
                          type="number"
                          min={1}
                          max={20}
                          value={reminderSettings.max_reminders}
                          onChange={e => setReminderSettings({ ...reminderSettings, max_reminders: parseInt(e.target.value) || 5 })}
                          className="w-14 px-2 py-1 text-[12px] text-right bg-foreground/5 border border-foreground/10 rounded-md text-foreground focus:outline-none focus:border-primary/40"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-[11px] text-muted-foreground/60 uppercase tracking-wider">CC assigned agent</span>
                        <label className="relative inline-block w-10 h-5">
                          <input
                            type="checkbox"
                            checked={reminderSettings.cc_agent}
                            onChange={() => setReminderSettings({ ...reminderSettings, cc_agent: !reminderSettings.cc_agent })}
                            className="opacity-0 w-0 h-0 peer"
                          />
                          <span className="absolute cursor-pointer inset-0 bg-foreground/10 rounded-full transition-colors duration-200 before:absolute before:content-[''] before:h-[14px] before:w-[14px] before:left-[3px] before:bottom-[3px] before:bg-muted-foreground before:rounded-full before:transition-all before:duration-200 peer-checked:bg-primary peer-checked:before:translate-x-5 peer-checked:before:bg-white" />
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-[11px] text-muted-foreground/60 uppercase tracking-wider">Enabled</span>
                        <label className="relative inline-block w-10 h-5">
                          <input
                            type="checkbox"
                            checked={reminderSettings.is_enabled}
                            onChange={() => setReminderSettings({ ...reminderSettings, is_enabled: !reminderSettings.is_enabled })}
                            className="opacity-0 w-0 h-0 peer"
                          />
                          <span className="absolute cursor-pointer inset-0 bg-foreground/10 rounded-full transition-colors duration-200 before:absolute before:content-[''] before:h-[14px] before:w-[14px] before:left-[3px] before:bottom-[3px] before:bg-muted-foreground before:rounded-full before:transition-all before:duration-200 peer-checked:bg-primary peer-checked:before:translate-x-5 peer-checked:before:bg-white" />
                        </label>
                      </div>
                    </div>

                    {/* Message + save */}
                    <div className="space-y-3">
                      <div>
                        <span className="text-[11px] text-muted-foreground/60 uppercase tracking-wider block mb-2">Custom message</span>
                        <textarea
                          rows={2}
                          value={reminderSettings.custom_message || ''}
                          onChange={e => setReminderSettings({ ...reminderSettings, custom_message: e.target.value || null })}
                          placeholder="Optional message to include in reminders..."
                          className="w-full px-2.5 py-1.5 text-[12px] bg-foreground/5 border border-foreground/10 rounded-md text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary/40 resize-none"
                        />
                      </div>
                      <Button
                        intent="primary"
                        size="sm"
                        onPress={handleSaveReminderSettings}
                        isDisabled={reminderSaving}
                        className="w-full"
                      >
                        {reminderSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                        {reminderSaving ? 'Saving...' : 'Save Reminders'}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
